import { config } from '../config';
import type {
  DiscoveryBucket,
  Foot,
  HypothesisPhase,
  HypothesisRow,
  MatchFormat,
  Status,
  TemplateEntry,
  TemplateSlot,
} from '../types';
import { assertNever } from './machine';
import { ok, reject, type DomainResult } from './result';

/**
 * PRE-1, PRE-4, PRE-5, PRE-7 — every pre-match **decision**, as pure functions.
 *
 * Design §6.0 describes one scrolling screen. Almost none of it is layout: the parts that can be
 * wrong are the shape list PRE-1 picks from, the `discoveryBucket` a hypothesis lands in (§3.2),
 * the substitution PRE-4's pickers perform, and the dedup/order/pre-check PRE-5 applies across a
 * team's history. Those live here, where they are testable in Node against `tsconfig.pure.json`'s
 * DOM-free build — and where the screen above them cannot quietly grow a second copy.
 *
 * Nothing in this module reads a clock, mints an id, or performs a write. `board.ts` already owns
 * position allocation and this module deliberately does not restate it: PRE-7's two exits are
 * `planLockPositions` (start locked) and *no position at all* (start in Discovery), which is what
 * keeps INV-4's "assigned exactly once" true across the Pre-Match and LOCK-4 paths together
 * (§6.0, §6.2).
 */

const MODULE = 'setup';

// ─────────────────────────────────────────────────────────────────────────────
// PRE-1 / PRE-8 — the three formation shape lists, and the arithmetic that keeps
// them honest (design §6.0a)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * PRE-8 — how many **outfield** players each format fields.
 *
 * **These are not §1.3 rows and must not become them.** §1.3 is NFR-9's inventory of *tunable
 * thresholds*; 6 / 8 / 10 are facts about the rules of association football, in the same category as
 * `SetPieceType`'s enum and in the same category as `HYPOTHESIS_CREATED_AT_UNAVAILABLE`, which was
 * explicitly ruled out of §1.3 for exactly this reason. Adding them would also redden
 * `spec13.test.ts` until the code followed, for a table that will never be tuned. Stated here so
 * nobody "completes" §1.3 with them later.
 *
 * **A type rather than a `const`, and that is not a formality.** §6.0a sketches this as a value with
 * `satisfies Record<MatchFormat, number>`; nothing reads it at runtime — the counts exist only to be
 * compared against a digit sum by the compiler — and a value nothing reads is a lint error here, for
 * good reason. `extends Record<MatchFormat, number>` keeps the exhaustiveness the `satisfies` bought:
 * adding a format to `MATCH_FORMATS` without a count here fails the build, which is the property
 * that matters. The observable contract — the build-failing message below — is identical.
 */
interface FieldPlayers extends Record<MatchFormat, number> {
  '7v7': 6;
  '9v9': 8;
  '11v11': 10;
}

/**
 * The escape hatch every list ends with, and the one shape exempt from the arithmetic.
 *
 * §6.0's reason is unchanged and applies three times over now: *without it the first unusual shape
 * becomes an argument for a text box, and a text box on this screen is the first step toward one on
 * a capture surface (PRE-3).* Because it is in all three lists it also **survives every format
 * change** — see {@link reconcileFormationGuess}.
 */
export const FORMATION_OTHER = 'other';

/**
 * One digit's worth of players, as a tuple whose `length` the compiler can add up.
 *
 * `1`–`9` rather than `1`–`5`: a digit outside the table makes `Sum` resolve to `never`, and
 * `ShapeFor` below has to treat that as a failure rather than a pass — so covering every digit a
 * formation could plausibly use keeps the failure branch for genuine nonsense.
 */
type Tally = {
  '1': [1];
  '2': [1, 1];
  '3': [1, 1, 1];
  '4': [1, 1, 1, 1];
  '5': [1, 1, 1, 1, 1];
  '6': [1, 1, 1, 1, 1, 1];
  '7': [1, 1, 1, 1, 1, 1, 1];
  '8': [1, 1, 1, 1, 1, 1, 1, 1];
  '9': [1, 1, 1, 1, 1, 1, 1, 1, 1];
};

/** `'3-3-2'` → `8`. `never` when any part is not a single digit `1`–`9`. */
type Sum<S extends string, Acc extends 1[] = []> = S extends `${infer H}-${infer R}`
  ? H extends keyof Tally
    ? Sum<R, [...Acc, ...Tally[H]]>
    : never
  : S extends keyof Tally
    ? [...Acc, ...Tally[S]]['length']
    : never;

/**
 * A shape, if its digits sum to `F`'s field-player count — otherwise a **sentence naming the defect**.
 *
 * Assigning the shape to this type is what fails the build, and the failure type is the diagnosis:
 *
 * ```
 * error TS2322: Type '"4-4-2"' is not assignable to type '"4-4-2 has 10 field players; the 9v9 list needs 8"'.
 * ```
 *
 * That message is a measurement, not a prediction — it is the diagnostic this repo's own `tsc`
 * emitted for `shapes('9v9')(['4-4-2'])` while this file was being written.
 */
type ShapeFor<F extends MatchFormat, S extends string> = S extends typeof FORMATION_OTHER
  ? S
  : [Sum<S>] extends [never]
    ? `${S} is not a formation: every part must be a single digit 1-9`
    : Sum<S> extends FieldPlayers[F]
      ? S
      : `${S} has ${Sum<S> & number} field players; the ${F} list needs ${FieldPlayers[F]}`;

/**
 * Checks one format's list **at its declaration site**, which is the only place the mistake is made.
 *
 * ── Why a type, and not a test ───────────────────────────────────────────────
 *
 * A `4-4-2` sitting in the 9v9 list is invisible to every other check this project has: it
 * type-checks, it lints, it round-trips through the archive, and it reaches the phone as a shape the
 * opponent cannot field. Prose cannot prevent it — no test evaluates a comment (§16.4's R-5 lesson).
 * A runtime assertion cannot either: a throw at module import fails on the device as readily as in
 * CI, which turns an editing mistake into a launch crash. §17's scope rule states the preference
 * order — *"a **type** that makes the violation unrepresentable; then a syntax-position lint; and
 * only then a module-dependency rule"* — and `npm run build` runs `check` before `vite build`.
 *
 * A unit test was considered and **rejected**: `npm test` and `npm run build` are deliberately
 * separate entry points, so a test leaves the build green on the very edit it exists to catch.
 *
 * The `_format` parameter exists only so `F` is inferred from a value rather than written twice.
 */
function shapes<F extends MatchFormat>(_format: F) {
  return <const S extends readonly string[]>(
    list: { readonly [K in keyof S]: ShapeFor<F, S[K] & string> },
  ): readonly string[] => list;
}

/**
 * PRE-1 / PRE-8 — the shapes offered, per format (design §6.0a).
 *
 * **Checked against how youth soccer is actually played, not composed from arithmetic.** Two
 * independent coaching sources per small-sided format, both stating the goalkeeper-excluded
 * convention explicitly. `3-3-2` is named the most common 9v9 shape by both; `3-2-3` is named the
 * recommended default for U11/U12; `2-3-1` is named the standard 7v7 shape by both. The 11v11 list
 * is unchanged — it was never wrong, it was only ever alone.
 *
 * **A formation's digits are outfield players only. The goalkeeper is assumed and never written.**
 * That is why this reads `4-4-2` and not `1-4-4-2`, and it is the single most likely way this table
 * gets corrupted: a minority of coaching sources *do* prefix the keeper (`1-3-3-2` for a 9v9
 * `3-3-2`), so a future editor working from one of those adds a shape whose digits sum to nine. The
 * rule is therefore enforced arithmetically by {@link shapes}, and stated as **visible text on the
 * picker itself** (`ui/prematch/TeamAndDetails.tsx`) rather than only here, where no user meets it.
 *
 * `1-3-2` is retained despite its leading `1` reading like a goalkeeper prefix, because excluding a
 * genuinely-played shape on a typographic worry is the wrong trade while the picker states the
 * convention in words directly above it. If that text is ever removed, this row breaks first.
 */
export const FORMATION_SHAPES = {
  '7v7': shapes('7v7')(['2-3-1', '3-2-1', '3-1-2', '2-1-2-1', '1-3-2', '3-1-1-1', FORMATION_OTHER]),
  '9v9': shapes('9v9')([
    '3-3-2',
    '3-2-3',
    '2-3-3',
    '4-3-1',
    '2-4-2',
    '3-1-3-1',
    '3-4-1',
    FORMATION_OTHER,
  ]),
  '11v11': shapes('11v11')([
    '4-4-2',
    '4-3-3',
    '4-2-3-1',
    '3-5-2',
    '3-4-3',
    '5-3-2',
    '4-1-4-1',
    FORMATION_OTHER,
  ]),
} as const satisfies Record<MatchFormat, readonly string[]>;

/**
 * **The guard's self-proof, and the reason it cannot be silently eroded.**
 *
 * This project has twice found a guard quietly weakened while every check stayed green (U-3's
 * flat-config `rules` clobbering; §16.4's R-5 false platform claim). An assertion that a guard
 * *exists* is worth nothing; only one that is red when the guard is absent is worth anything.
 *
 * `4-4-2` is a real 11v11 shape mis-filed into the 9v9 list — the exact editing mistake. If
 * `ShapeFor` ever stops rejecting it, this directive becomes an *"Unused '@ts-expect-error'"* error
 * and **the build fails for the opposite reason**. Deleting the guard therefore cannot be done
 * quietly; deleting this line is itself the visible edit.
 */
// @ts-expect-error — a 10-player shape in the 9v9 list must not compile (§6.0a).
const _MISFILED_SHAPE_MUST_NOT_COMPILE = shapes('9v9')(['4-4-2']);

/**
 * PRE-1 / PRE-8 — the shapes a match may currently offer.
 *
 * **Empty while no format is set**, which the picker renders as its own reason rather than as an
 * empty menu: *"A picker with an empty menu is indistinguishable from a broken one"* (§6.0a). The
 * emptiness is also what makes {@link isFormationFor} refuse every shape before a format is chosen,
 * so "no shape is offerable and none can be stored" (PRE-8) is one rule rather than two.
 */
export function shapesFor(format: MatchFormat | null): readonly string[] {
  return format === null ? [] : FORMATION_SHAPES[format];
}

// ─────────────────────────────────────────────────────────────────────────────
// SES-5 × PRE-8 — the half-length default follows the format (F-41, ruled)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * SES-5's default half length, **per format** — §19 F-41, ruled by the stakeholder.
 *
 * `HALF_LENGTH_DEFAULT = 35` is an 11v11 figure. 9v9 and 7v7 are played in shorter halves, so a
 * single default is *wrong for two formats in three* — and a default that is usually wrong is a
 * stepper the user must remember to correct, on the one screen where forgetting is silent and the
 * consequence (every derived match minute, SES-4) is not. At his daughter's age group the
 * stakeholder would otherwise correct the stepper on **every single match**.
 *
 * **This changes where the stepper starts, and nothing else.** SES-5's range
 * `[HALF_LENGTH_MIN_MIN, HALF_LENGTH_MIN_MAX] = [20, 45]` is unchanged and reaches all three, so no
 * value becomes unreachable and the field stays fully adjustable. Two alternatives were offered and
 * rejected: keeping 35 for everything, and asking every time with no default at all.
 *
 * ── Why this is not three §1.3 rows ─────────────────────────────────────────
 *
 * The same ruling `FIELD_PLAYERS` got, for the same reason, which is why it sits beside it: 25 and
 * 30 are conventions of an age group, not thresholds anyone will want to retune, and §1.3 is NFR-9's
 * inventory of *tunables*. Adding them would also redden `spec13.test.ts` until the code followed,
 * for a table that will never be tuned.
 *
 * **The `11v11` row reads the existing constant rather than restating `35`**, so SES-5's literal
 * *"default 35"* keeps exactly one home, `spec13.test.ts` needs no edit, and the two numbers that
 * are new are the only two spelled here. The asymmetry is the point: one of these three is a
 * requirement with a §1.3 row already, and two are conventions this table owns.
 */
const HALF_LENGTH_BY_FORMAT = {
  '7v7': 25,
  '9v9': 30,
  '11v11': config.HALF_LENGTH_DEFAULT,
} as const satisfies Record<MatchFormat, number>;

/** SES-5's starting value for a format. Always inside `[HALF_LENGTH_MIN_MIN, …_MAX]` (asserted). */
export function halfLengthForFormat(format: MatchFormat): number {
  return HALF_LENGTH_BY_FORMAT[format];
}

/**
 * What the half length becomes when the format changes — or `null` for *"leave it alone"*.
 *
 * **A default decides where the stepper starts, and it has already started once the user has moved
 * it.** So the format's default is applied only while the value is still a default: the creation
 * placeholder (no format chosen yet — the stepper has not been shown, let alone touched), or the
 * outgoing format's own default. A value the user set themselves is theirs and survives.
 *
 * *Alternative considered:* overwrite unconditionally — **rejected**. Guessing whether someone who
 * typed 32 for a 9v9 match wants 35 or 32 at 11v11 is a coin flip, and destroying an explicit input
 * is strictly worse than keeping one that is visible on screen and one tap from either answer.
 * *Alternative considered:* a "user has touched it" column — rejected, it is a schema change and an
 * archive change to record something the stored value already tells us.
 *
 * Returned as a decision rather than applied, so `handleUpdateMatchSetup` can put it in the **same**
 * patch op as the format itself — one transaction, exactly as `reconcileFormationGuess` is.
 */
export function reconcileHalfLength(
  previousFormat: MatchFormat | null,
  nextFormat: MatchFormat,
  storedHalfLength: number,
): number | null {
  // No format yet ⇒ the stored value is the placeholder `createMatch` wrote, which no user has ever
  // seen (the stepper renders no number until a format is set). Always replaced.
  const untouched =
    previousFormat === null || storedHalfLength === halfLengthForFormat(previousFormat);
  if (!untouched) return null;
  const next = halfLengthForFormat(nextFormat);
  return next === storedHalfLength ? null : next;
}

/**
 * PRE-8 — is this shape offerable by **this** format?
 *
 * **This replaces the union-wide predicate; it does not join it.** The former `isFormationGuess`
 * widened across all three lists would accept `4-2-3-1` on a 9v9 match — a weaker door left standing
 * beside the new one (`bidirectional-analysis`: the check has to run in the direction the data
 * actually arrives). The old signature must not survive anywhere.
 */
export function isFormationFor(format: MatchFormat | null, value: string): boolean {
  return shapesFor(format).includes(value);
}

/**
 * PRE-8 — what a stored `formationGuess` becomes when the format changes.
 *
 * Setting a format after a shape was already chosen is reachable: the format control and the shape
 * picker are both live for the whole of Pre-Match. **The guess is cleared if and only if the stored
 * value is not offerable by the new format.** `other` is in every list and therefore survives every
 * change; `3-4-3` does not survive a switch to 9v9; `3-3-2` does not survive a switch to 11v11.
 *
 * *Alternative considered:* refuse the format change while a guess is set — rejected, it traps the
 * user behind their own earlier tap. *Alternative considered:* clear unconditionally — rejected, it
 * discards `other`, which is never wrong.
 *
 * Returned as a decision rather than applied, so `handleUpdateMatchSetup` can put the clear in the
 * **same** patch op — one transaction, per §6.0a.
 */
export function reconcileFormationGuess(
  nextFormat: MatchFormat,
  storedGuess: string | null,
): string | null {
  if (storedGuess === null) return null;
  return isFormationFor(nextFormat, storedGuess) ? storedGuess : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// §3.1 — `Match.date` is a calendar date
// ─────────────────────────────────────────────────────────────────────────────

/** `YYYY-MM-DD`, with the month and day inside their ranges. */
const CALENDAR_DATE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/**
 * §3.1 — *"Calendar date as `YYYY-MM-DD`, **not** a timestamp … The fixed-width form also sorts
 * lexicographically, which is what FILE-3's history ordering wants."*
 *
 * PRE-5's recency compare and FILE-1's match history both rely on that lexicographic property, so a
 * malformed date is not cosmetic — it silently reorders a team's history. Validated here rather than
 * left to the `<input type="date">` that normally produces it, because an imported or hand-edited
 * value reaches the same column.
 *
 * **Bound, stated:** this checks shape and field ranges, not calendar reality — `2026-02-30` passes.
 * Catching that needs a real date, and `domain/` may not construct one (NFR-9/INV-7 lint). The
 * property the rest of the app depends on is fixed-width lexicographic ordering, and that holds.
 */
export function isCalendarDate(value: string): boolean {
  return CALENDAR_DATE.test(value);
}

// ─────────────────────────────────────────────────────────────────────────────
// §3.2 — the `discoveryBucket` assignment rule
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Design §3.2's second source: **derived from `phase`**, for every path where no template is
 * involved — PRE-3's free-text creation and (U-14's) flag promotion.
 *
 * DISC-2 gives Discovery four pages while `Hypothesis.phase` has seven values, so `gk`,
 * `individual` and `setPiece` would otherwise have no page and be untappable. The mapping is
 * **total** and closed by `assertNever`, so adding a phase later breaks the build rather than
 * silently producing an unreachable hypothesis.
 *
 * A template entry that wants a different page says so: §3.2's source **1** (an explicit
 * `discoveryBucket` on the seed entry) wins, and the seeded library uses that for the GK entry that
 * happens while the opponent has the ball and for the individual stub about set pieces.
 */
export function discoveryBucketForPhase(phase: HypothesisPhase): DiscoveryBucket {
  switch (phase) {
    case 'inPossession':
      return 'inPossession';
    case 'outOfPossession':
      return 'outOfPossession';
    case 'attTransition':
      return 'attTransition';
    case 'defTransition':
      return 'defTransition';
    // The opponent's keeper has the ball, so you are out of possession.
    case 'gk':
      return 'outOfPossession';
    // Most individual patterns are observed while the opponent plays.
    case 'individual':
      return 'outOfPossession';
    // Set pieces are not a Discovery page (DISC-2).
    case 'setPiece':
      return 'defTransition';
    default:
      return assertNever(phase);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PRE-4 — fill-in-the-blank stubs
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One resolved picker value.
 *
 * **ENV-11 is the shape of this union.** *"The schema provides no field for a player's name or
 * physical appearance. Players are identified by shirt number only."* There are two kinds because
 * the design names two pickers — a number wheel and a left/right segmented control — and there is
 * no third kind to add without inventing a field the schema does not have.
 */
export type SlotValue =
  | { readonly kind: 'shirtNumber'; readonly number: number }
  | { readonly kind: 'foot'; readonly foot: Foot };

export interface ResolvedStub {
  /** The template text with every `{key}` substituted. */
  readonly text: string;
  /** The `Player` this hypothesis binds to, by shirt number (FILE-2). `null` when no number slot. */
  readonly shirtNumber: number | null;
  /** Carried onto the `Player` row when the stub named a foot; `null` otherwise. */
  readonly foot: Foot | null;
}

/** `{key}` — the placeholder form the seed library uses. */
function placeholder(key: string): string {
  return `{${key}}`;
}

/**
 * PRE-4 — substitutes picker values into a stub's text.
 *
 * > *"Fill-in-the-blank player stubs (e.g. "#__ always finishes __ foot") resolve via pickers
 * > (number wheel, left/right), producing a hypothesis bound to a Player row, created on demand."*
 *
 * Every rejection below is a state the UI can reach only by dispatching an incomplete resolution,
 * and each is returned rather than thrown so the caller can keep the sheet open on the offending
 * slot. **At most one slot of each kind** is permitted: a stub naming two shirt numbers has no
 * single `Player` to bind to, and picking one silently would attach the hypothesis to the wrong
 * player forever (FILE-2's linkage is cross-match, so the error would outlive the match).
 */
export function resolveTemplateText(
  text: string,
  slots: readonly TemplateSlot[],
  values: Readonly<Record<string, SlotValue>>,
): DomainResult<ResolvedStub> {
  for (const kind of ['shirtNumber', 'foot'] as const) {
    const named = slots.filter((slot) => slot.kind === kind);
    if (named.length > 1) {
      return reject(
        MODULE,
        null,
        'ERR_SLOT_AMBIGUOUS',
        `This stub asks for ${String(named.length)} ${kind === 'shirtNumber' ? 'shirt numbers' : 'feet'}, so there is no single player to attach it to.`,
        { kind, keys: named.map((slot) => slot.key) },
      );
    }
  }

  let resolved = text;
  let shirtNumber: number | null = null;
  let foot: Foot | null = null;

  for (const slot of slots) {
    const value = values[slot.key];
    if (value === undefined || value.kind !== slot.kind) {
      return reject(
        MODULE,
        null,
        'ERR_SLOT_UNRESOLVED',
        'Finish filling in the blanks before adding this one.',
        { key: slot.key, expected: slot.kind, got: value?.kind ?? null },
      );
    }

    if (!resolved.includes(placeholder(slot.key))) {
      return reject(
        MODULE,
        null,
        'ERR_SLOT_UNRESOLVED',
        'This stub is malformed: one of its blanks does not appear in the text.',
        { key: slot.key, text },
      );
    }

    if (value.kind === 'shirtNumber') {
      if (
        !Number.isInteger(value.number) ||
        value.number < config.SHIRT_NUMBER_MIN ||
        value.number > config.SHIRT_NUMBER_MAX
      ) {
        return reject(
          MODULE,
          null,
          'ERR_SHIRT_NUMBER_RANGE',
          `A shirt number must be between ${String(config.SHIRT_NUMBER_MIN)} and ${String(config.SHIRT_NUMBER_MAX)}.`,
          { key: slot.key, number: value.number },
        );
      }
      shirtNumber = value.number;
      resolved = resolved.split(placeholder(slot.key)).join(String(value.number));
    } else {
      foot = value.foot;
      resolved = resolved.split(placeholder(slot.key)).join(value.foot);
    }
  }

  return ok({ text: resolved, shirtNumber, foot });
}

/** How an unfilled blank is shown to the user — PRE-4's own notation. */
const BLANK = '__';

/**
 * PRE-4 — the label an unresolved stub shows in the library.
 *
 * The requirement writes its example as *"#__ always finishes __ foot"*, and that is what the row
 * must say. `{n}` and `{foot}` are the seed file's substitution syntax: developer notation, correct
 * in the JSON and wrong on a screen. Found by looking at the rendered library in a browser — the
 * Node tests were asserting the *resolved* sentence, which was right, and never saw the label.
 */
export function stubDisplayText(text: string, slots: readonly TemplateSlot[]): string {
  return slots.reduce((shown, slot) => shown.split(placeholder(slot.key)).join(BLANK), text);
}

// ─────────────────────────────────────────────────────────────────────────────
// PRE-5 — carry-forward
// ─────────────────────────────────────────────────────────────────────────────

/** The two statuses PRE-5 carries forward. `tracking` and `disproved` are not offered. */
export type CarriedStatus = Extract<Status, 'confirmed' | 'invalidated'>;

/** What `deriveCarryForward` needs off a prior match's `Hypothesis` row, and nothing more. */
export interface PriorHypothesis {
  readonly id: string;
  readonly matchId: string;
  readonly text: string;
  readonly phase: HypothesisPhase;
  readonly discoveryBucket: DiscoveryBucket;
  readonly playerId: string | null;
  readonly status: Status;
  readonly cachedCount: number;
}

/**
 * A prior match of the same Team.
 *
 * **`state` is deliberately absent from this type.** PRE-5 (rev 2): *"Availability is keyed on
 * hypothesis status from any prior match of that Team **regardless of that match's state**; it MUST
 * NOT require the prior match to be `archived`."* A field the filter cannot see is a filter that
 * cannot be written by mistake — and the mistake is reachable, because distillation is asynchronous
 * (SES-11) and a team may be scouted twice before either match is distilled.
 */
export interface PriorMatch {
  readonly id: string;
  /** `YYYY-MM-DD`. Fixed width, so a lexicographic compare is a chronological one (§3.1). */
  readonly date: string;
  /**
   * PRE-8 — the format that match was played in. **Carried so it can be labeled, never filtered.**
   *
   * See {@link isCrossFormatCarry}. `null` for a match that never left Pre-Match with a format set,
   * and for every match written before revision 3 on a device whose archive predates `v003`.
   */
  readonly format: MatchFormat | null;
}

/** One de-duplicated carry-forward candidate — design §6.0's "confirmed in 3 of 4 matches" row. */
export interface CarryCandidate {
  /** The normalized text the dedup collapsed on. Stable across matches; safe as a React key. */
  readonly key: string;
  readonly text: string;
  readonly phase: HypothesisPhase;
  readonly discoveryBucket: DiscoveryBucket;
  readonly playerId: string | null;
  /** §3.2's F-21 snapshot — provenance, written once at creation and never maintained. */
  readonly carriedFromMatchId: string;
  readonly carriedStatus: CarriedStatus;
  readonly carriedCount: number;
  /**
   * PRE-8 — the format of the match this candidate was carried from, for the label (§6.0).
   *
   * Part of the same snapshot: it describes the match the pattern was observed in, so it is read off
   * `carriedFromMatchId`'s row and never re-derived later.
   */
  readonly carriedFormat: MatchFormat | null;
  /** The two halves of the "in 3 of 4 matches" label. */
  readonly confirmedInMatches: number;
  readonly invalidatedInMatches: number;
  /** How many prior matches of this Team exist at all — the denominator. */
  readonly priorMatchCount: number;
  /** PRE-5 — the top `BOARD_CAP` arrive pre-checked; the rest are listed unchecked. */
  readonly preChecked: boolean;
}

/**
 * PRE-5's dedup key — *"normalized text (lowercase, collapsed whitespace, trimmed punctuation)"*.
 *
 * Typographic quotes and dashes are folded to their ASCII forms first. Only **edge** punctuation is
 * stripped: an internal `#` or `-` is part of the pattern ("#9 does not track back" must not
 * collide with "#19 does not track back"), while a trailing full stop is a typing accident.
 */
export function normalizeHypothesisText(text: string): string {
  return text
    .replace(/[‘’ʼ]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, '-')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/^[^\p{L}\p{N}]+/u, '')
    .replace(/[^\p{L}\p{N}]+$/u, '')
    .trim();
}

/**
 * Most recent first, then by id descending so the order is total (§6.1's F-11 lesson).
 *
 * Takes only the two fields it compares rather than a whole `PriorMatch`. That is what lets the
 * callers below fall back to `{id, date: ''}` for a hypothesis whose match is missing (a dangling
 * `carriedFromMatchId` is legal, F-21) **without inventing a `format` for a match nobody has** —
 * PRE-8's `null` there is a fact, and a synthetic stand-in would launder it into a label.
 */
function byMostRecentMatch(
  a: Pick<PriorMatch, 'id' | 'date'>,
  b: Pick<PriorMatch, 'id' | 'date'>,
): number {
  if (a.date !== b.date) return a.date < b.date ? 1 : -1;
  return a.id < b.id ? 1 : -1;
}

function isCarried(status: Status): status is CarriedStatus {
  return status === 'confirmed' || status === 'invalidated';
}

/**
 * PRE-5 — the carry-forward candidate list for a previously scouted Team.
 *
 * > *"for a previously scouted Team, offer its `confirmed` hypotheses, and its `invalidated` ones
 * > distinctly labeled, from prior matches as pre-checked board candidates, carrying
 * > `carriedFromMatchId`."*
 *
 * Four behaviors, each of which the caller must not re-derive:
 *
 * 1. **No filter on match state.** The caller supplies every prior match of the Team; `PriorMatch`
 *    carries no `state` for the filter to reach. See that type's note.
 * 2. **Dedup by normalized text**, so a pattern seen in three prior meetings cannot burn three of
 *    eight board slots (§6.0). The surviving row's display text is the most recent occurrence's.
 * 3. **`carriedStatus` is `confirmed` if the pattern was ever confirmed**, and the snapshot's
 *    `carriedFromMatchId` / `carriedCount` are taken from the most recent occurrence *with that
 *    status* — so a "confirmed" badge always quotes a match in which it was confirmed. Both tallies
 *    travel on the candidate, so the label can be honest about a mixed history.
 * 4. **Order and pre-check**: confirmed before invalidated, prior count descending, most recent
 *    match first; the top `BOARD_CAP` arrive checked. Ties fall through to the normalized key so the
 *    order is total and the same history always produces the same list.
 *
 * Hypotheses whose status is `tracking` or `disproved` are not offered. `disproved` means "I was
 * wrong" (§3.3), and `tracking` never crossed the threshold.
 *
 * **5. No filter on `format` either, and that is deliberate** (PRE-8, §6.0). Filtering carry-forward
 * by format would delete a team's whole history the season it ages up — precisely the case PRE-8
 * exists for — and PRE-5's MUST says "from prior matches" without qualification. The cost is that a
 * *"their 3 at the back push high"* observed in 9v9 says nothing about the 11v11 back four the same
 * club fields next season, so every candidate carries {@link CarryCandidate.carriedFormat} and the
 * screen labels the ones that differ. The label is the whole mechanism; the judgement stays with the
 * user. See {@link isCrossFormatCarry}.
 */
export function deriveCarryForward(
  priorHypotheses: readonly PriorHypothesis[],
  priorMatches: readonly PriorMatch[],
): CarryCandidate[] {
  const matchById = new Map(priorMatches.map((match) => [match.id, match]));
  const groups = new Map<string, PriorHypothesis[]>();

  for (const row of priorHypotheses) {
    if (!isCarried(row.status)) continue;
    if (!matchById.has(row.matchId)) continue; // not a match of this team
    const key = normalizeHypothesisText(row.text);
    if (key === '') continue;
    const group = groups.get(key);
    if (group === undefined) groups.set(key, [row]);
    else group.push(row);
  }

  const recency = (row: PriorHypothesis): Pick<PriorMatch, 'id' | 'date'> =>
    matchById.get(row.matchId) ?? { id: row.matchId, date: '' };

  const candidates = [...groups.entries()].map(([key, rows]): Omit<CarryCandidate, 'preChecked'> => {
    const newestFirst = [...rows].sort((a, b) => byMostRecentMatch(recency(a), recency(b)));
    const confirmed = newestFirst.filter((row) => row.status === 'confirmed');
    const invalidated = newestFirst.filter((row) => row.status === 'invalidated');

    const carriedStatus: CarriedStatus = confirmed.length > 0 ? 'confirmed' : 'invalidated';
    // Non-null by construction: `carriedStatus` is chosen from whichever list is non-empty, and a
    // group exists only because at least one row survived the `isCarried` filter above.
    const source = (carriedStatus === 'confirmed' ? confirmed[0] : invalidated[0]) as PriorHypothesis;
    const display = newestFirst[0] as PriorHypothesis;

    return {
      key,
      text: display.text,
      phase: display.phase,
      discoveryBucket: display.discoveryBucket,
      playerId: display.playerId,
      carriedFromMatchId: source.matchId,
      carriedStatus,
      carriedCount: source.cachedCount,
      carriedFormat: matchById.get(source.matchId)?.format ?? null,
      confirmedInMatches: new Set(confirmed.map((row) => row.matchId)).size,
      invalidatedInMatches: new Set(invalidated.map((row) => row.matchId)).size,
      priorMatchCount: priorMatches.length,
    };
  });

  candidates.sort((a, b) => {
    if (a.carriedStatus !== b.carriedStatus) return a.carriedStatus === 'confirmed' ? -1 : 1;
    if (a.carriedCount !== b.carriedCount) return b.carriedCount - a.carriedCount;
    const byMatch = byMostRecentMatch(
      matchById.get(a.carriedFromMatchId) ?? { id: a.carriedFromMatchId, date: '' },
      matchById.get(b.carriedFromMatchId) ?? { id: b.carriedFromMatchId, date: '' },
    );
    if (byMatch !== 0) return byMatch;
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  });

  return candidates.map((candidate, index) => ({
    ...candidate,
    preChecked: index < config.BOARD_CAP,
  }));
}

/**
 * PRE-8 / PRE-5 — does this candidate come from a match played in a **different** format?
 *
 * The one decision behind §6.0's cross-format label, named here rather than spelled as a comparison
 * on the screen — the same reason `requiresBoardChoice` exists. An unlabeled cross-format candidate
 * is a worse failure than a filtered one, because nothing on screen would say so.
 *
 * `false` when either side is unknown: a candidate whose source format is `null` (a match that never
 * had one) has nothing to announce, and before the user has chosen this match's format there is
 * nothing to differ *from*. Both are absences, not conflicts, and labelling an absence would put a
 * badge on every row of a first-ever setup.
 */
export function isCrossFormatCarry(
  setupFormat: MatchFormat | null,
  carry: Pick<CarryCandidate, 'carriedFormat'>,
): boolean {
  if (setupFormat === null || carry.carriedFormat === null) return false;
  return carry.carriedFormat !== setupFormat;
}

// ─────────────────────────────────────────────────────────────────────────────
// The selection model — one candidate shape, three sources
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hypothesis the user has selected at Pre-Match, before the match's board is committed.
 *
 * PRE-2 (a template row), PRE-3 (free text) and PRE-5 (carry-forward) are three ways to *reach* the
 * same thing, and the differences between them are three nullable provenance fields — not three
 * shapes. Collapsing them here is what lets PRE-7's selection surface, the `BOARD_CAP` counter and
 * `toHypothesisRow` each be written once.
 *
 * **`id` is minted when the candidate is selected, not when the board is committed.** PRE-7's
 * chooser hands `StartCapture` a list of ids, so an id assigned at commit time would leave the
 * chooser referring to rows that do not exist yet. This is §5.1's "build the row at the call site"
 * discipline applied off the tap path, and it is why deselecting is free: an unused id costs
 * nothing because nothing was written.
 */
export interface PreMatchCandidate {
  readonly id: string;
  readonly text: string;
  readonly phase: HypothesisPhase;
  readonly discoveryBucket: DiscoveryBucket;
  /** PRE-4 / FILE-2 — set when a shirt-number slot was resolved; `null` otherwise. */
  readonly playerId: string | null;
  /** PRE-2 — the library group this came from; `null` for free text and carry-forward. */
  readonly sourceTemplateId: string | null;
  /** PRE-5 — the F-21 snapshot. All three are `null` together or set together. */
  readonly carriedFromMatchId: string | null;
  readonly carriedStatus: Status | null;
  readonly carriedCount: number | null;
}

/**
 * PRE-2 — a candidate from a library entry, with any PRE-4 slots already resolved.
 *
 * `resolved` is the output of {@link resolveTemplateText}; for an entry with no slots the caller
 * passes the entry's own text. The bucket comes from the **entry**, not from the phase: §3.2's
 * source 1 wins, which is how the seeded GK entry about distribution reaches the `inPossession`
 * page while every other GK entry reaches `outOfPossession`.
 */
export function candidateFromTemplateEntry(
  id: string,
  templateId: string,
  entry: {
    readonly text: string;
    readonly phase: HypothesisPhase;
    readonly discoveryBucket: DiscoveryBucket;
  },
  resolvedText: string,
  playerId: string | null,
): PreMatchCandidate {
  return {
    id,
    text: resolvedText,
    phase: entry.phase,
    discoveryBucket: entry.discoveryBucket,
    playerId,
    sourceTemplateId: templateId,
    carriedFromMatchId: null,
    carriedStatus: null,
    carriedCount: null,
  };
}

/**
 * PRE-3 — a candidate the user typed, with a phase chosen from a picker.
 *
 * §3.2's source **2**: no template is involved, so the bucket is derived from the phase. Typing is
 * unrestricted here and at Halftime, and nowhere else (PRE-3, LIVE-9).
 */
export function candidateFromFreeText(
  id: string,
  text: string,
  phase: HypothesisPhase,
): PreMatchCandidate {
  return {
    id,
    text,
    phase,
    discoveryBucket: discoveryBucketForPhase(phase),
    playerId: null,
    sourceTemplateId: null,
    carriedFromMatchId: null,
    carriedStatus: null,
    carriedCount: null,
  };
}

/**
 * PRE-5 — a candidate carried forward from a prior match of the same Team.
 *
 * The three `carried*` fields travel together as the F-21 snapshot: *"what the user knew at the
 * moment they chose to carry it"*. They are written once here and **never maintained against the
 * source**, which is the point — disproving the source hypothesis in an old match's triage in
 * November must not silently change a badge on a live tile (design §3.2).
 */
export function candidateFromCarry(id: string, carry: CarryCandidate): PreMatchCandidate {
  return {
    id,
    text: carry.text,
    phase: carry.phase,
    discoveryBucket: carry.discoveryBucket,
    playerId: carry.playerId,
    sourceTemplateId: null,
    carriedFromMatchId: carry.carriedFromMatchId,
    carriedStatus: carry.carriedStatus,
    carriedCount: carry.carriedCount,
  };
}

/**
 * The `HypothesisRow` a candidate becomes — the one place a Pre-Match hypothesis is constructed.
 *
 * Every derived field starts at its zero: **`cachedCount = 0`, `statusOverride = null`, so `status`
 * is `tracking` per INV-3, and `boardPosition = null`.** §6.0 is explicit that a carried candidate
 * arrives this way — *"its prior confirmation surfaces through the non-status carry badge"* — so a
 * pattern confirmed five times in September does not start today's match already amber. A carried
 * `confirmed` written into `status` would also launder a prior match's count into this match's
 * render order (LIVE-12, SC-6).
 *
 * `boardPosition` is `null` on **both** PRE-7 exits: "start locked" assigns positions through the
 * ops `handleStartCapture` emits, so this function never assigns one and INV-4's "exactly once" has
 * one writer rather than two.
 *
 * ── `createdAt` is a parameter, and that is not a formality ──────────────────
 *
 * §3.2's `v002` column is DISC-4's third sort term, and this is the one place a `Hypothesis` is
 * constructed — so this is the one place it can be written. It is **passed in** because `domain/` may
 * not read a clock (NFR-6: `Date` is lint-rejected here, for the same reason SES-1 carries its anchor
 * in rather than sampling completion time).
 *
 * Callers must supply values that are **distinct across a batch created together**, or §6.1's
 * promise fails in the exact place it was written for: before kickoff every row ties on terms 1 and
 * 2, so rows sharing a `createdAt` fall through to `id asc` — random UUID order — which is the defect
 * the column exists to remove. `app/preMatch.startCapture` does this by offsetting one clock reading
 * by each row's index in the user's own selection order.
 */
export function toHypothesisRow(
  matchId: string,
  candidate: PreMatchCandidate,
  createdAt: number,
): HypothesisRow {
  return {
    id: candidate.id,
    matchId,
    text: candidate.text,
    phase: candidate.phase,
    discoveryBucket: candidate.discoveryBucket,
    playerId: candidate.playerId,
    statusOverride: null,
    cachedCount: 0,
    lastIncrementTs: null,
    createdAt,
    status: 'tracking',
    boardPosition: null,
    sourceTemplateId: candidate.sourceTemplateId,
    carriedFromMatchId: candidate.carriedFromMatchId,
    carriedStatus: candidate.carriedStatus,
    carriedCount: candidate.carriedCount,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// PRE-7 — the board-selection question
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// PRE-9 — the library arrives pre-selected (design §6.0b)
// ─────────────────────────────────────────────────────────────────────────────

/** One seeded entry the Pre-Match screen opens with already checked, and the template it came from. */
export interface PreSelectableEntry {
  readonly templateId: string;
  readonly entry: TemplateEntry;
}

/**
 * PRE-9 — *"every seeded built-in entry belonging to the four moment templates and to the goalkeeper
 * template is pre-selected … Entries carrying unresolved PRE-4 slots are excluded."*
 *
 * ── The predicate is `slots === undefined`, NOT a list of template names ────
 *
 * §6.0b is explicit that this is *"a mechanism rather than an enumeration"*, and states the reason:
 * an entry with unresolved slots has **no text** until PRE-4's pickers run, so pre-adding it would
 * put a literal `#{n} always finishes {foot} foot` on a Discovery page. Today the two formulations
 * select the same 49 rows — the seven slotted entries are exactly the *Individual stubs* template —
 * and **they diverge the moment someone adds a slotted goalkeeper entry**. The mechanism is the one
 * that stays right, and it makes PRE-4's contract hold by construction: the range of this selector
 * cannot contain an unresolved stub, so no list needs maintaining to keep it out.
 *
 * `builtin` is the other half of *"seeded built-in"*: PRE-6 lets the user save their own board as a
 * template, and a user template is a deliberate choice they made once, not a default to re-apply to
 * every future opponent.
 *
 * Library order is preserved, and that is load-bearing rather than incidental: `startCapture`
 * stamps `createdAt` as one clock reading offset by each candidate's index (R-11), and before
 * kickoff every row ties on DISC-4's first two terms — so this order **is** the order the four
 * Discovery pages open in (§6.1).
 */
export function preSelectableEntries(
  templates: readonly {
    readonly id: string;
    readonly builtin: boolean;
    readonly hypothesisTexts: readonly TemplateEntry[];
  }[],
): PreSelectableEntry[] {
  const out: PreSelectableEntry[] = [];
  for (const template of templates) {
    if (!template.builtin) continue;
    for (const entry of template.hypothesisTexts) {
      if (entry.slots !== undefined) continue;
      out.push({ templateId: template.id, entry });
    }
  }
  return out;
}

/**
 * PRE-9 × PRE-5 — the merge, decided by **PRE-5's own normalizer**.
 *
 * > *"where a carry-forward candidate's normalized text equals a pre-selected seed entry's, exactly
 * > one candidate exists and it is the carried one."*
 *
 * A carried hypothesis almost always *originated* from a template, so its text is identical to a
 * seed entry's — and PRE-5 already defines normalization for its own within-Team dedup. The same
 * function decides this, applied across the two sources: one function, one direction, no second copy
 * of the comparison. {@link normalizeHypothesisText} has exactly two call sites for exactly that
 * reason, and this is the second.
 *
 * The carried form wins because it is strictly more informative — it brings `carriedFromMatchId`,
 * `carriedStatus`, `carriedCount` and §6.5's carry badge, and the seed row has nothing to add.
 *
 * **The seed's position in the list is what the survivor takes**, so the Discovery pages open in
 * library order whether or not a row was carried; carried candidates matching no seed are appended.
 * Nothing about a returning opponent should reshuffle the pages a new one gets.
 */
export function mergePreSelectionWithCarried(
  seeds: readonly PreMatchCandidate[],
  carried: readonly PreMatchCandidate[],
): PreMatchCandidate[] {
  const carriedByKey = new Map<string, PreMatchCandidate>();
  for (const candidate of carried) {
    const key = normalizeHypothesisText(candidate.text);
    if (!carriedByKey.has(key)) carriedByKey.set(key, candidate);
  }

  const used = new Set<string>();
  const merged = seeds.map((seed) => {
    const key = normalizeHypothesisText(seed.text);
    const carry = carriedByKey.get(key);
    if (carry === undefined) return seed;
    used.add(key);
    return carry;
  });

  // The tail iterates the **map**, not the input array, so "exactly once" is a property of this
  // function's output rather than a property PRE-5's own dedup happens to supply upstream. Two
  // carried candidates that normalize alike are unreachable through `deriveCarryForward` today —
  // and an output contract that depends on its caller having already enforced it is the shape of a
  // claimed invariant. Found by the test that asserts it.
  for (const [key, candidate] of carriedByKey) {
    if (!used.has(key)) merged.push(candidate);
  }
  return merged;
}

/**
 * PRE-7 — *"If more than `BOARD_CAP` candidates are selected at Pre-Match, the user chooses which 8
 * receive a `boardPosition`."*
 *
 * The predicate the selection surface branches on, named once so the screen never spells the
 * comparison itself (NFR-9). Eight or fewer is the other branch: all of them receive positions and
 * the session may start `locked` directly, which is what LOCK-7 makes legal.
 */
export function requiresBoardChoice(selectedCount: number): boolean {
  return selectedCount > config.BOARD_CAP;
}

/**
 * The chooser's starting point when more than `BOARD_CAP` are selected — **PRE-9 changed what this
 * may be** (§6.0b).
 *
 * It was selection order, truncated. Under PRE-9 the selection opens with 49 seeded candidates, so
 * selection order would pre-tick the first eight rows of the *In possession* template and call them
 * a board — eight permanent second-half slots (INV-4 assigns once) handed to patterns nobody has
 * seen, by an arbitrary rule, on the exit whose whole purpose is to commit to eight.
 *
 * §6.0b's ruling: *"carried candidates keep PRE-5's existing pre-check ordering; **where no
 * candidate carries a prior-match signal the chooser opens with nothing pre-selected** and the exit
 * refuses until ≤ `BOARD_CAP` are chosen, naming the reason."* So the default is the prior-match
 * signal and nothing else — the friction lands exactly where it belongs, because nobody should start
 * *locked* against an opponent they have never seen.
 *
 * Order still matters and is still the caller's: it becomes `boardPosition` 0..k−1 through
 * `planLockPositions` (§6.2).
 */
export function defaultPositionedIds(selected: readonly PreMatchCandidate[]): string[] {
  return selected
    .filter((candidate) => candidate.carriedFromMatchId !== null)
    .slice(0, config.BOARD_CAP)
    .map((candidate) => candidate.id);
}

/**
 * PRE-7 × PRE-9 — may the *Start locked* exit proceed with this choice?
 *
 * `null` when it may; a sentence naming the reason when it may not. Only the **locked** exit is
 * gated: §6.0b scopes PRE-7's choosing to that exit because *"the choosing exists to assign
 * `boardPosition`"*, and *Start in Discovery* assigns none — so requiring it there was always
 * spending a screen for nothing, and PRE-9 (revision 4) says so in the requirement itself.
 *
 * The refusal fires only where the chooser exists at all. At or below `BOARD_CAP` every candidate is
 * positioned and there is no question to answer (LOCK-7 makes a short board legal); above it, an
 * empty choice is not a short board, it is an unanswered question, and starting locked would land
 * the user on an empty grid with 49 hypotheses they can only reach by leaving it.
 */
export function refuseLockedStart(
  selected: readonly PreMatchCandidate[],
  positionedCount: number,
): string | null {
  if (!requiresBoardChoice(selected.length)) return null;
  if (positionedCount > 0) return null;
  return `Choose up to ${String(config.BOARD_CAP)} of these to start on the board, or start in Discovery instead.`;
}
